import { gql, TypedDocumentNode, useMutation } from '@apollo/client';
import {
  AssignedPermission,
  UpdateAccountPermissionMutation,
  UpdateAccountPermissionMutationVariables,
} from '../../generated/graphql';

export const updateAccountPermissionMutation: TypedDocumentNode<
  UpdateAccountPermissionMutation,
  UpdateAccountPermissionMutationVariables
> = gql`
  mutation UpdateAccountPermission($email: String!, $permission: AssignedPermissionInput!) {
    updateAccountPermission(email: $email, permission: $permission) {
      id
      permissionId
      granted
    }
  }
`;

export type UpdateAccountPermission = (
  email: string,
  permissionId: string,
  granted: boolean,
) => Promise<AssignedPermission>;

export function useUpdateAccountPermission(): UpdateAccountPermission {
  const [update] = useMutation(updateAccountPermissionMutation);

  return async (email, permissionId, granted) => {
    // id functions as cache key; in case of accounts, it is the SHA-1 of the email and the permission id
    const source = new TextEncoder().encode(email + permissionId);
    const digest = await crypto.subtle.digest('SHA-1', source);
    const result = [...new Uint8Array(digest)];
    const id = result.map((digit) => digit.toString(16).padStart(2, '0')).join('');

    const { data, errors } = await update({
      variables: {
        email,
        permission: {
          permissionId,
          granted,
        },
      },
      optimisticResponse: {
        updateAccountPermission: {
          __typename: 'AssignedPermission',
          id,
          permissionId,
          granted,
        },
      },
    });

    if (errors?.length) {
      throw errors[0];
    }

    if (!data) {
      // Should not happen: https://github.com/apollographql/apollo-client/issues/6374
      throw new Error('Expected data, nothing received.');
    }

    return data.updateAccountPermission;
  };
}
